import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilterLegacy/types'
import { useDepartureDateStore } from '@/infrastructure/departure-date/useDepartureDateStore'
import { useIntl } from '@/intl'
import { createSelectDateFilterActions } from '@/scopes/search/search/domain/actions/selectDateFilterActions'
import { useSearchDependencies } from '@/scopes/search/search/pages/SearchPage/context'
import { getMiddleDate, isDateRangeWholeMonth } from '@sunwebgroup/ui-components'
import * as React from 'react'

export function useDepartureDateFilter({ options }: { options: DepartureDateFilterOptions }) {
  const intl = useIntl()
  const {
    view,
    setView,
    departureDate,
    setDepartureDate,
    flexibleRange,
    setFlexibleRange,
    originDate,
    setOriginDate,
  } = useDepartureDateStore()

  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const selectDepartureDateActions = createSelectDateFilterActions({ filterSelectionQueryParamHandler })

  const availableDates = options.map(({ value }) => value)
  const maxDateString = availableDates[availableDates.length - 1]
  const minDateString = availableDates[0]

  const formattedText = React.useMemo(() => {
    if (!departureDate)
      return

    if (!Array.isArray(departureDate)) {
      return { long: intl.formatDate(departureDate, { day: 'numeric', month: 'long' }) }
    }

    const wholeMonthIsSelected = isDateRangeWholeMonth(departureDate, minDateString, maxDateString)

    if (wholeMonthIsSelected) {
      const [startDateString] = departureDate
      const month = intl.formatDate(startDateString, { month: 'long' })
      return {
        long: `${month.charAt(0).toUpperCase() + month.slice(1)} ${intl.formatDate(startDateString, {
          year: 'numeric',
        })}`,
      }
    }

    // This is the case where we are selecting a specific date range (which is only possible when the range is given outside of the filter)
    if (flexibleRange === 'exact') {
      const startDate = new Date(departureDate[0])
      const endDate = new Date(departureDate[1])
      const isDifferentMonth = startDate.getMonth() !== endDate.getMonth()

      if (isDifferentMonth) {
        return {
          long: `${intl.formatDate(startDate, { day: 'numeric', month: 'short' })} - ${intl.formatDate(endDate, {
            day: 'numeric',
            month: 'short',
          })}`,
        }
      }

      return {
        long: `${intl.formatDate(startDate, { day: 'numeric' })} - ${intl.formatDate(endDate, {
          day: 'numeric',
          month: 'short',
        })}`,
      }
    }

    const numberOfDays = Number.parseInt(flexibleRange)
    const selectedDate = intl.formatDate(getMiddleDate(departureDate), {
      day: 'numeric',
      month: 'short',
    })
    const addedDays = intl.formatMessage(
      { id: 'quicksearch_filter_departure_date_number_of_days' },
      { n_days: numberOfDays },
    )

    return {
      long: `${selectedDate} ${intl.formatMessage(
        { id: 'quicksearch_filter_departure_date_chips_number_of_days' },
        { n_days: numberOfDays },
      )}`,
      short: `${selectedDate} ${addedDays}`,
    }
  }, [departureDate, flexibleRange, intl, maxDateString, minDateString])

  const resetDepartureDate = () => {
    selectDepartureDateActions.setDepartureDateFilterSelection(undefined)
  }

  return {
    departureDate,
    setDepartureDate,
    flexibleRange,
    setFlexibleRange,
    availableDates,
    maxDateString,
    minDateString,
    originDate,
    setOriginDate,
    view,
    setView,
    formattedText,
    resetDepartureDate,
  }
}
